import { QueryResult } from '@apollo/client'
import { Button, message, Result } from 'antd'
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { CustomerTypeEnum, User, UserContext } from '../gen/models'
import { isCloakedUser } from '../utils/cloak'
import { useMeQuery, useOnLoginMutation } from '../hooks/users'
// @ts-ignore
import Env from '../gen/Env'
import { PageLoading } from '../../common/components/PageLoading'
import { LaunchDarklyFeatures } from '../../common/constants'
import { useLogout } from '../../common/hooks/useLogout'

const defaultProps = {}

type Props = Partial<typeof defaultProps> & { children?: React.ReactNode }

export type MeConsumerData = UserContext

type MeConsumerResponse = {
  me: MeConsumerData
}

type MeContextType = Partial<QueryResult<MeConsumerResponse>> & {
  refetchData?(): void
}

type MeWithoutPaymentContextType = Partial<QueryResult<User>> & {
  refetchData?(): void
}

export const MeContext = React.createContext<MeContextType>({})

export const MeWithoutPaymentContext = React.createContext<MeWithoutPaymentContextType>({})

const MeWithoutPaymentConsumer = MeWithoutPaymentContext.Consumer

const MeProvider: React.FC<Props> = ({ children }) => {
  const { data, loading, error, refetch } = useMeQuery()
  const logout = useLogout()
  const location = useLocation()
  const [onLogin] = useOnLoginMutation()
  const featureFlags = useFlags()
  const ldClient = useLDClient()
  const [supplierFlagsReady, setSupplierFlagsReady] = useState(false)

  const me = data?.me
  const isSupplier = data?.me.user.customer.type === CustomerTypeEnum.Supplier
  // #7806 — a cloaked principal's portal access does not depend on the flag, so they neither await
  // identify nor get ejected. Deliberately NOT hasSupplierPortalAccess(): the provider's decision is
  // two-staged (hold, then eject) and only the EXEMPTION is new here.
  const isCloaked = isCloakedUser(data?.me.user)
  const email = data?.me.user.email
  const supplierPortalEnabled = featureFlags[LaunchDarklyFeatures.supplierPortal]
  const onLoginState = location.state?.onLogin
  // #7843 — `onLogin` carries no `@AllowSupplier()`, so for a REAL supplier the server refuses it
  // structurally (403) and nothing on the client reads its result. Gate it on exactly that population:
  // NOT `isSupplier && !isAdmin`, because a cloaked admin reports `isAdmin: false` and IS admitted (via
  // `isOverride`) — and what `onLogin` does for them is their OWN login tracking, not the supplier's.
  // Flag-free by construction: this provider sits AT the identify point (react-conventions §12.1).
  const isRealSupplier = isSupplier && me?.isAdmin !== true && !isCloaked

  useEffect(() => {
    if (onLoginState && !loading && me && !isRealSupplier) {
      onLogin().then(() => {})
    }
  }, [onLogin, loading, me, onLoginState, isRealSupplier])

  useEffect(() => {
    if (error) {
      message.error(error.message)
    }
  }, [error])

  useEffect(() => {
    if (!ldClient || !isSupplier || !email || isCloaked) {
      return
    }
    ldClient
      .identify({ kind: 'user', key: email })
      .then(() => setSupplierFlagsReady(true))
      .catch(() => {
        // Flag state for the identified user never became known — the loading gate below keeps
        // holding (never eject and never render the portal on unknown flag state). Swallowing the
        // rejection is required: an unhandled identify rejection is a hard error in the JS SDK.
        console.warn('LaunchDarkly identify failed for supplier user; holding on loading state')
      })
  }, [ldClient, isSupplier, email, isCloaked])

  if (loading) {
    return <PageLoading />
  }

  if (error) {
    return (
      <MeContext.Provider value={{ data, loading, error, refetchData: refetch }}>
        <Result
          status="error"
          title="Failed to load account"
          subTitle={error.message}
          extra={
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <span style={{ color: 'rgba(0, 0, 0, 0.45)' }}>Do you have another account?</span>
              <Button type="primary" onClick={logout}>
                Sign in with a different account
              </Button>
            </div>
          }
        />
      </MeContext.Provider>
    )
  }

  // #7806 — the cloaked supplier-typed principal skips BOTH the hold and the eject (AC-L1a/AC-L1b);
  // the body is otherwise unchanged, so every principal the flag still governs is unaffected.
  if (isSupplier && !isCloaked) {
    if (!supplierFlagsReady) {
      return <PageLoading />
    }
    if (!supplierPortalEnabled) {
      window.location.href = Env.supplierPortalBaseUrl
      return <></>
    }
  }

  return <MeContext.Provider value={{ data, loading, error, refetchData: refetch }}>{children}</MeContext.Provider>
}

export { MeProvider, MeWithoutPaymentConsumer }
